// useDashboardData.js
// Returns all dashboard data filtered/sliced by the selected period key.
// Period keys: 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastYear' | 'allTime'

import { useMemo } from "react";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const today = new Date("2026-08-10");

const fmt = (d) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const subtractDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() - n);
  return r;
};

// Readable label for the trigger button
export const PERIOD_LABELS = {
  today:     "Today",
  last7:     "Last 7 Days",
  last30:    "Last 30 Days",
  thisMonth: "This Month",
  lastYear:  "Last Year",
  allTime:   "All Time",
};

// Sub-label shown below the button (date range string)
export const PERIOD_SUBLABELS = {
  today:     `${fmt(today)}`,
  last7:     `${fmt(subtractDays(today, 6))} – ${fmt(today)}`,
  last30:    `${fmt(subtractDays(today, 29))} – ${fmt(today)}`,
  thisMonth: `01 Aug 2026 – ${fmt(today)}`,
  lastYear:  `01 Aug 2025 – ${fmt(today)}`,
  allTime:   "All time data",
};

// ─── Per-period stat totals ───────────────────────────────────────────────────

const STATS = {
  today:     { totalEmployees: 1248, contractEmployees: 284, activeContracts: 987, employmentTypes: 12, workSchedules: 24, contractsExpiring: 3,  employmentChanges: 2  },
  last7:     { totalEmployees: 1248, contractEmployees: 284, activeContracts: 987, employmentTypes: 12, workSchedules: 24, contractsExpiring: 8,  employmentChanges: 14 },
  last30:    { totalEmployees: 1248, contractEmployees: 284, activeContracts: 987, employmentTypes: 12, workSchedules: 24, contractsExpiring: 18, employmentChanges: 46 },
  thisMonth: { totalEmployees: 1248, contractEmployees: 284, activeContracts: 987, employmentTypes: 12, workSchedules: 24, contractsExpiring: 18, employmentChanges: 46 },
  lastYear:  { totalEmployees: 1140, contractEmployees: 260, activeContracts: 920, employmentTypes: 10, workSchedules: 20, contractsExpiring: 72, employmentChanges: 310 },
  allTime:   { totalEmployees: 1248, contractEmployees: 284, activeContracts: 987, employmentTypes: 12, workSchedules: 24, contractsExpiring: 95, employmentChanges: 580 },
};

// ─── Workforce movement (bar chart) ──────────────────────────────────────────

const WORKFORCE_ALL = [
  { month: "Feb '26", newHires: 28, exits: 8  },
  { month: "Mar '26", newHires: 35, exits: 12 },
  { month: "Apr '26", newHires: 42, exits: 10 },
  { month: "May '26", newHires: 55, exits: 15 },
  { month: "Jun '26", newHires: 48, exits: 9  },
  { month: "Jul '26", newHires: 60, exits: 11 },
  { month: "Aug '26", newHires: 52, exits: 13 },
];

const WORKFORCE_LAST_YEAR = [
  { month: "Aug '25", newHires: 18, exits: 5  },
  { month: "Oct '25", newHires: 22, exits: 7  },
  { month: "Dec '25", newHires: 30, exits: 10 },
  { month: "Feb '26", newHires: 28, exits: 8  },
  { month: "Apr '26", newHires: 42, exits: 10 },
  { month: "Jun '26", newHires: 48, exits: 9  },
  { month: "Aug '26", newHires: 52, exits: 13 },
];

const WORKFORCE_TODAY = [
  { month: "08:00", newHires: 0, exits: 0 },
  { month: "10:00", newHires: 1, exits: 0 },
  { month: "12:00", newHires: 1, exits: 1 },
  { month: "14:00", newHires: 2, exits: 0 },
  { month: "16:00", newHires: 0, exits: 1 },
  { month: "18:00", newHires: 1, exits: 0 },
];

const WORKFORCE_7 = WORKFORCE_ALL.slice(-3).concat([
  { month: "Aug 8",  newHires: 4,  exits: 1 },
  { month: "Aug 9",  newHires: 3,  exits: 2 },
  { month: "Aug 10", newHires: 2,  exits: 0 },
]);

const WORKFORCE_30 = WORKFORCE_ALL.slice(-5);

// ─── Employment type breakdown ────────────────────────────────────────────────

const ET_ALLTIME = [
  { name: "Permanent", value: 742, percentage: 59.5, color: "#3b82f6" },
  { name: "Contract",  value: 284, percentage: 22.8, color: "#10b981" },
  { name: "Probation", value: 152, percentage: 12.2, color: "#8b5cf6" },
  { name: "Intern",    value: 70,  percentage: 5.6,  color: "#f59e0b" },
];

const ET_TODAY = [
  { name: "Permanent", value: 742, percentage: 60.1, color: "#3b82f6" },
  { name: "Contract",  value: 281, percentage: 22.5, color: "#10b981" },
  { name: "Probation", value: 155, percentage: 12.4, color: "#8b5cf6" },
  { name: "Intern",    value: 70,  percentage: 5.0,  color: "#f59e0b" },
];

const ET_LAST_YEAR = [
  { name: "Permanent", value: 680, percentage: 59.6, color: "#3b82f6" },
  { name: "Contract",  value: 260, percentage: 22.8, color: "#10b981" },
  { name: "Probation", value: 135, percentage: 11.8, color: "#8b5cf6" },
  { name: "Intern",    value: 65,  percentage: 5.7,  color: "#f59e0b" },
];

// ─── Contract status ─────────────────────────────────────────────────────────

const CS_ALLTIME = [
  { label: "Active",            count: 1105, percentage: 72.1, color: "#3b82f6" },
  { label: "Draft",             count: 85,   percentage: 5.5,  color: "#10b981" },
  { label: "Pending Signature", count: 42,   percentage: 2.7,  color: "#f59e0b" },
  { label: "Expired",           count: 16,   percentage: 1.0,  color: "#f97316" },
  { label: "Terminated",        count: 0,    percentage: 0.0,  color: "#ef4444" },
];

const CS_TODAY = [
  { label: "Active",            count: 987, percentage: 79.1, color: "#3b82f6" },
  { label: "Draft",             count: 85,  percentage: 6.8,  color: "#10b981" },
  { label: "Pending Signature", count: 42,  percentage: 3.4,  color: "#f59e0b" },
  { label: "Expired",           count: 16,  percentage: 1.3,  color: "#f97316" },
  { label: "Terminated",        count: 0,   percentage: 0.0,  color: "#ef4444" },
];

const CS_LAST_YEAR = [
  { label: "Active",            count: 920, percentage: 80.7, color: "#3b82f6" },
  { label: "Draft",             count: 110, percentage: 9.6,  color: "#10b981" },
  { label: "Pending Signature", count: 58,  percentage: 5.1,  color: "#f59e0b" },
  { label: "Expired",           count: 42,  percentage: 3.7,  color: "#f97316" },
  { label: "Terminated",        count: 10,  percentage: 0.9,  color: "#ef4444" },
];

// ─── Work schedule distribution ───────────────────────────────────────────────

const WS_ALLTIME = [
  { name: "General Shift",  count: 520, percentage: 41.7 },
  { name: "Flexible Shift", count: 310, percentage: 24.8 },
  { name: "Night Shift",    count: 245, percentage: 19.6 },
  { name: "Weekend Shift",  count: 110, percentage: 8.8  },
  { name: "Others",         count: 63,  percentage: 5.1  },
];

const WS_TODAY = [
  { name: "General Shift",  count: 430, percentage: 43.5 },
  { name: "Flexible Shift", count: 260, percentage: 26.3 },
  { name: "Night Shift",    count: 190, percentage: 19.2 },
  { name: "Weekend Shift",  count: 80,  percentage: 8.1  },
  { name: "Others",         count: 27,  percentage: 2.7  },
];

const WS_LAST_YEAR = [
  { name: "General Shift",  count: 480, percentage: 42.1 },
  { name: "Flexible Shift", count: 285, percentage: 25.0 },
  { name: "Night Shift",    count: 220, percentage: 19.3 },
  { name: "Weekend Shift",  count: 95,  percentage: 8.3  },
  { name: "Others",         count: 60,  percentage: 5.3  },
];

// ─── Recent activity (per period) ────────────────────────────────────────────

const RA_TODAY = [
  { id: "a1", icon: "contract", title: "New contract created",    subtitle: "Rahul Sharma",    time: "10:30 AM", color: "blue"   },
  { id: "a2", icon: "type",     title: "Employment type updated", subtitle: "Senior Developer",time: "09:45 AM", color: "green"  },
  { id: "a3", icon: "schedule", title: "Work schedule assigned",  subtitle: "Neha Gupta",      time: "08:15 AM", color: "purple" },
];

const RA_7 = [
  { id: "a1", icon: "contract", title: "New contract created",    subtitle: "Rahul Sharma",    time: "10:30 AM",    color: "blue"   },
  { id: "a2", icon: "type",     title: "Employment type updated", subtitle: "Senior Developer",time: "09:45 AM",    color: "green"  },
  { id: "a3", icon: "schedule", title: "Work schedule assigned",  subtitle: "Neha Gupta",      time: "Yesterday",   color: "purple" },
  { id: "a4", icon: "alert",    title: "Contract expired",        subtitle: "Amit Verma",      time: "Aug 8, 2026", color: "orange" },
  { id: "a5", icon: "history",  title: "History record added",    subtitle: "Riya Malhotra",   time: "Aug 7, 2026", color: "red"    },
];

const RA_ALL = [
  { id: "a1", icon: "contract", title: "New contract created",    subtitle: "Rahul Sharma",    time: "10:30 AM",    color: "blue"   },
  { id: "a2", icon: "type",     title: "Employment type updated", subtitle: "Senior Developer",time: "09:45 AM",    color: "green"  },
  { id: "a3", icon: "schedule", title: "Work schedule assigned",  subtitle: "Neha Gupta",      time: "Yesterday",   color: "purple" },
  { id: "a4", icon: "alert",    title: "Contract expired",        subtitle: "Amit Verma",      time: "Yesterday",   color: "orange" },
  { id: "a5", icon: "history",  title: "History record added",    subtitle: "Riya Malhotra",   time: "Aug 3, 2026", color: "red"    },
];

const RA_LAST_YEAR = [
  { id: "b1", icon: "contract", title: "Annual contracts renewed",  subtitle: "Bulk renewal",     time: "Jan 2026",    color: "blue"   },
  { id: "b2", icon: "type",     title: "New employment type added", subtitle: "Remote Part-Time", time: "Mar 2026",    color: "green"  },
  { id: "b3", icon: "schedule", title: "Shift restructure complete",subtitle: "Night Shift",      time: "May 2026",    color: "purple" },
  { id: "b4", icon: "alert",    title: "72 contracts expiring",     subtitle: "Pending review",   time: "Jun 2026",    color: "orange" },
  { id: "b5", icon: "history",  title: "310 employment changes",    subtitle: "Year total",       time: "Aug 9, 2026", color: "red"    },
];

// ─── Upcoming contract expiry ─────────────────────────────────────────────────

const CE_ALL = [
  { id: "c1", name: "Rahul Sharma",  contractId: "CT-1001", contractType: "Permanent", expiryDate: "Aug 15, 2026", daysLeft: 11, avatar: "RS", typeColor: "blue"   },
  { id: "c2", name: "Neha Gupta",    contractId: "CT-1045", contractType: "Contract",  expiryDate: "Aug 22, 2026", daysLeft: 18, avatar: "NG", typeColor: "green"  },
  { id: "c3", name: "Amit Verma",    contractId: "CT-1058", contractType: "Permanent", expiryDate: "Sep 05, 2026", daysLeft: 32, avatar: "AV", typeColor: "blue"   },
  { id: "c4", name: "Riya Malhotra", contractId: "CT-1077", contractType: "Probation", expiryDate: "Sep 18, 2026", daysLeft: 45, avatar: "RM", typeColor: "purple" },
];

const CE_TODAY = [
  { id: "c1", name: "Rahul Sharma",  contractId: "CT-1001", contractType: "Permanent", expiryDate: "Aug 15, 2026", daysLeft: 11, avatar: "RS", typeColor: "blue"  },
];

const CE_7 = [
  { id: "c1", name: "Rahul Sharma",  contractId: "CT-1001", contractType: "Permanent", expiryDate: "Aug 15, 2026", daysLeft: 11, avatar: "RS", typeColor: "blue"  },
  { id: "c2", name: "Neha Gupta",    contractId: "CT-1045", contractType: "Contract",  expiryDate: "Aug 22, 2026", daysLeft: 18, avatar: "NG", typeColor: "green" },
];

const CE_LAST_YEAR = [
  { id: "c1", name: "Rahul Sharma",  contractId: "CT-1001", contractType: "Permanent", expiryDate: "Aug 15, 2026", daysLeft: 11,  avatar: "RS", typeColor: "blue"   },
  { id: "c2", name: "Neha Gupta",    contractId: "CT-1045", contractType: "Contract",  expiryDate: "Aug 22, 2026", daysLeft: 18,  avatar: "NG", typeColor: "green"  },
  { id: "c3", name: "Amit Verma",    contractId: "CT-1058", contractType: "Permanent", expiryDate: "Sep 05, 2026", daysLeft: 32,  avatar: "AV", typeColor: "blue"   },
  { id: "c4", name: "Riya Malhotra", contractId: "CT-1077", contractType: "Probation", expiryDate: "Sep 18, 2026", daysLeft: 45,  avatar: "RM", typeColor: "purple" },
  { id: "c5", name: "Sanya Malhotra",contractId: "CT-0980", contractType: "Contract",  expiryDate: "Oct 01, 2026", daysLeft: 58,  avatar: "SM", typeColor: "green"  },
  { id: "c6", name: "Deepak Jain",   contractId: "CT-0911", contractType: "Permanent", expiryDate: "Nov 15, 2026", daysLeft: 103, avatar: "DJ", typeColor: "blue"   },
];

// ─── Alerts ───────────────────────────────────────────────────────────────────

const ALERTS = {
  today:     [{ id: "al1", count: 2,  label: "New Hires Today",       icon: "newHires",  color: "blue"   }, { id: "al2", count: 1, label: "Probations Ending Soon", icon: "probation", color: "purple" }],
  last7:     [{ id: "al1", count: 9,  label: "New Hires This Week",    icon: "newHires",  color: "blue"   }, { id: "al2", count: 3, label: "Probations Ending Soon", icon: "probation", color: "purple" }],
  last30:    [{ id: "al1", count: 18, label: "New Hires This Month",   icon: "newHires",  color: "blue"   }, { id: "al2", count: 5, label: "Probations Ending Soon", icon: "probation", color: "purple" }],
  thisMonth: [{ id: "al1", count: 18, label: "New Hires This Month",   icon: "newHires",  color: "blue"   }, { id: "al2", count: 5, label: "Probations Ending Soon", icon: "probation", color: "purple" }],
  lastYear:  [{ id: "al1", count: 108,label: "New Hires This Year",    icon: "newHires",  color: "blue"   }, { id: "al2", count: 22,label: "Probations Ending Soon", icon: "probation", color: "purple" }],
  allTime:   [{ id: "al1", count: 580,label: "Total New Hires (All Time)",icon:"newHires",color: "blue"   }, { id: "al2", count: 47,label: "Total Probations Ended",  icon: "probation", color: "purple" }],
};

// ─── Main hook ────────────────────────────────────────────────────────────────

const DATA_MAP = {
  today:     { workforce: WORKFORCE_TODAY, et: ET_TODAY,     cs: CS_TODAY,     ws: WS_TODAY,     ra: RA_TODAY, ce: CE_TODAY     },
  last7:     { workforce: WORKFORCE_7,     et: ET_ALLTIME,   cs: CS_ALLTIME,   ws: WS_ALLTIME,   ra: RA_7,     ce: CE_7         },
  last30:    { workforce: WORKFORCE_30,    et: ET_ALLTIME,   cs: CS_ALLTIME,   ws: WS_ALLTIME,   ra: RA_ALL,   ce: CE_ALL       },
  thisMonth: { workforce: WORKFORCE_30,    et: ET_ALLTIME,   cs: CS_ALLTIME,   ws: WS_ALLTIME,   ra: RA_ALL,   ce: CE_ALL       },
  lastYear:  { workforce: WORKFORCE_LAST_YEAR, et: ET_LAST_YEAR, cs: CS_LAST_YEAR, ws: WS_LAST_YEAR, ra: RA_LAST_YEAR, ce: CE_LAST_YEAR },
  allTime:   { workforce: WORKFORCE_ALL,   et: ET_ALLTIME,   cs: CS_ALLTIME,   ws: WS_ALLTIME,   ra: RA_ALL,   ce: CE_LAST_YEAR },
};

/**
 * Returns all dashboard data sliced by the given period key.
 * @param {string} period - one of 'today'|'last7'|'last30'|'thisMonth'|'lastYear'|'allTime'
 */
const useDashboardData = (period = "thisMonth") => {
  return useMemo(() => {
    const d = DATA_MAP[period] || DATA_MAP.thisMonth;
    return {
      stats:        STATS[period] || STATS.thisMonth,
      workforce:    d.workforce,
      employmentTypes: d.et,
      contractStatus:  d.cs,
      workSchedules:   d.ws,
      recentActivity:  d.ra,
      contractExpiry:  d.ce,
      alerts:          ALERTS[period] || ALERTS.thisMonth,
    };
  }, [period]);
};

export default useDashboardData;
